import { applyI18n, t } from '../i18n';
import { formatBytes } from '../i18n/formatBytes';
import {
  fileTypeIconFor,
  fileTypeLabelKey,
  resolveFilePreview,
  type FilePreviewResolution,
} from '../files/fileTypes';
import { fileTypeImg } from './fileTypeImg';

export interface ArchiveEntry {
  name: string;
  size: number | null;
  resolved: FilePreviewResolution;
  getBytes: () => Promise<Uint8Array>;
}

export function renderArchiveListing(
  content: HTMLElement,
  entries: ArchiveEntry[],
  options?: { embedded?: boolean },
): void {
  const wrap = document.createElement('div');
  wrap.className = options?.embedded
    ? 'file-preview-archive file-preview-archive--embedded'
    : 'file-preview-archive';
  const tableWrap = document.createElement('div');
  tableWrap.className = 'file-preview-table-wrap';
  const table = document.createElement('table');
  table.className = 'data-table file-preview-table';

  const thead = document.createElement('thead');
  thead.innerHTML = `<tr>
    <th data-i18n="files.preview.archive.col.name"></th>
    <th data-i18n="files.preview.archive.col.type"></th>
    <th data-i18n="files.preview.archive.col.size"></th>
    <th class="data-table__actions-heading"></th>
  </tr>`;
  table.append(thead);

  const tbody = document.createElement('tbody');
  table.append(tbody);

  const limited = entries.slice(0, 200);
  for (const entry of limited) {
    const tr = document.createElement('tr');
    const nameTd = document.createElement('td');
    const icon = fileTypeImg(fileTypeIconFor(entry.name, entry.resolved.mime), 16);
    if (canPreviewArchiveEntry(entry.resolved.kind)) {
      const previewButton = document.createElement('button');
      previewButton.type = 'button';
      previewButton.className = 'files-table__name-button';
      previewButton.dataset.archiveAction = 'open';
      previewButton.append(icon, document.createTextNode(entry.name));
      previewButton.addEventListener('click', async () => {
        const bytes = await entry.getBytes();
        const entryBlob = new Blob([toArrayBuffer(bytes)], { type: entry.resolved.mime });
        const { showFilePreview } = await import('./filePreviewDialog');
        await showFilePreview(entry.name, entryBlob, entry.resolved.mime);
      });
      nameTd.append(previewButton);
    } else {
      nameTd.append(icon, document.createTextNode(entry.name));
    }

    const typeTd = document.createElement('td');
    typeTd.textContent = t(fileTypeLabelKey(entry.resolved));

    const sizeTd = document.createElement('td');
    sizeTd.textContent = entry.size != null ? formatBytes(entry.size) : '—';

    const actionsTd = document.createElement('td');
    actionsTd.className = 'data-table__actions';
    const actions = document.createElement('div');
    actions.className = 'data-table__actions-inner';
    const downloadButton = document.createElement('button');
    downloadButton.type = 'button';
    downloadButton.className = 'button ghost';
    downloadButton.dataset.archiveAction = 'download';
    downloadButton.textContent = t('sedr.evidence.download');
    downloadButton.addEventListener('click', async () => {
      const bytes = await entry.getBytes();
      downloadBlob(entry.name, new Blob([toArrayBuffer(bytes)], { type: entry.resolved.mime }));
    });
    actions.append(downloadButton);
    actionsTd.append(actions);

    tr.append(nameTd, typeTd, sizeTd, actionsTd);
    tbody.append(tr);
  }

  tableWrap.append(table);
  wrap.append(tableWrap);
  content.append(wrap);
  applyI18n(wrap);
}

export function canPreviewArchiveEntry(kind: string): boolean {
  return kind !== 'binary';
}

export function resolveArchiveEntry(name: string, bytes: Uint8Array): FilePreviewResolution {
  const header = bytes.subarray(0, Math.min(bytes.length, 512));
  return resolveFilePreview(name, null, header);
}

function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
}

function downloadBlob(fileName: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.rel = 'noopener';
  link.click();
  URL.revokeObjectURL(url);
}
